import { Plus, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { TestQuestion, TestQuestionOption, TestQuestionType } from "@/types/test";

interface Props {
  questions: TestQuestion[];
  onChange: (q: TestQuestion[]) => void;
}

const TYPES: TestQuestionType[] = ["short_text", "long_text", "single_choice", "multi_choice", "likert"];

function blankQuestion(type: TestQuestionType): TestQuestion {
  const id = crypto.randomUUID();
  if (type === "short_text" || type === "long_text") {
    return { id, type, text: "", required: false };
  }
  return {
    id,
    type,
    text: "",
    required: false,
    options: [
      { value: "0", label: "" },
      { value: "1", label: "" },
    ],
  };
}

export function TestQuestionEditor({ questions, onChange }: Props) {
  const { t } = useTranslation();

  const update = (idx: number, patch: Partial<TestQuestion>) => {
    const next = [...questions];
    next[idx] = { ...next[idx], ...patch } as TestQuestion;
    onChange(next);
  };
  const remove = (idx: number) => onChange(questions.filter((_, i) => i !== idx));
  const add = (type: TestQuestionType) => onChange([...questions, blankQuestion(type)]);

  const updateOption = (qIdx: number, oIdx: number, patch: Partial<TestQuestionOption>) => {
    const q = questions[qIdx];
    if (!q) return;
    if (!("options" in q)) return;
    const opts = [...q.options];
    opts[oIdx] = { ...opts[oIdx]!, ...patch };
    update(qIdx, { options: opts } as Partial<TestQuestion>);
  };
  const addOption = (qIdx: number) => {
    const q = questions[qIdx];
    if (!q) return;
    if (!("options" in q)) return;
    update(qIdx, {
      options: [...q.options, { value: String(q.options.length), label: "" }],
    } as Partial<TestQuestion>);
  };
  const removeOption = (qIdx: number, oIdx: number) => {
    const q = questions[qIdx];
    if (!q) return;
    if (!("options" in q)) return;
    update(qIdx, { options: q.options.filter((_, i) => i !== oIdx) } as Partial<TestQuestion>);
  };

  return (
    <div className="space-y-4">
      {questions.map((q, qIdx) => (
        <article
          key={q.id}
          className="group relative overflow-hidden rounded-xl border border-border/70 bg-card p-5 transition-colors hover:border-primary/40"
        >
          <span aria-hidden className="absolute inset-y-3 left-0 w-[2px] rounded-r bg-primary/30" />
          <div className="flex items-start gap-3 pl-2">
            <span
              className="mt-2 w-10 shrink-0 font-heading text-2xl font-medium italic tabular-nums leading-none text-primary/70"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {String(qIdx + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0 flex-1">
              <Input
                type="text"
                value={q.text}
                onChange={(e) => update(qIdx, { text: e.target.value })}
                placeholder={t("tests.admin.questionText")}
                className="h-11 border-x-0 border-t-0 border-b border-border/70 bg-transparent px-0 text-base shadow-none focus-visible:border-primary focus-visible:ring-0"
              />
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <span className="rounded-full border border-border bg-secondary/60 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {t(`tests.admin.questionTypes.${q.type}`)}
                </span>
                <Label
                  htmlFor={`q-${q.id}-required`}
                  className="text-[12px] font-medium tracking-wide text-muted-foreground"
                >
                  <Checkbox
                    id={`q-${q.id}-required`}
                    checked={q.required}
                    onCheckedChange={(v) => update(qIdx, { required: v === true })}
                  />
                  {t("tests.admin.required")}
                </Label>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => remove(qIdx)}
              aria-label={t("tests.admin.removeQuestion")}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash />
            </Button>
          </div>

          {(q.type === "single_choice" || q.type === "multi_choice" || q.type === "likert") && (
            <div className="mt-4 space-y-2 border-t border-dashed border-border/60 pl-12 pt-4">
              {q.options.map((o, oIdx) => (
                <div
                  key={oIdx}
                  className="flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-secondary/40"
                >
                  <span
                    className="w-6 font-heading text-[11px] italic tabular-nums leading-none text-muted-foreground/70"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {String.fromCharCode(97 + oIdx)}.
                  </span>
                  <Input
                    type="text"
                    value={o.value}
                    onChange={(e) => updateOption(qIdx, oIdx, { value: e.target.value })}
                    placeholder={t("tests.admin.optionValue")}
                    className="h-8 w-20 text-xs"
                  />
                  <Input
                    type="text"
                    value={o.label}
                    onChange={(e) => updateOption(qIdx, oIdx, { label: e.target.value })}
                    placeholder={t("tests.admin.optionLabel")}
                    className="h-8 flex-1 text-sm"
                  />
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={o.weight ?? ""}
                    onChange={(e) =>
                      updateOption(qIdx, oIdx, {
                        weight: e.target.value === "" ? undefined : Number(e.target.value),
                      })
                    }
                    placeholder={t("tests.admin.weight")}
                    className="h-8 w-16 text-xs tabular-nums"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => removeOption(qIdx, oIdx)}
                    className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => addOption(qIdx)}
                className="px-0 text-[12px] uppercase tracking-[0.14em]"
              >
                + {t("tests.admin.addOption")}
              </Button>
            </div>
          )}
        </article>
      ))}

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-border/80 bg-secondary/30 p-3">
        <span className="ml-1 mr-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Add
        </span>
        {TYPES.map((type) => (
          <Button
            key={type}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => add(type)}
            className="rounded-full"
          >
            <Plus />
            {t(`tests.admin.questionTypes.${type}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}
